import axios from 'axios';
import posthog from 'posthog-js';
import { ENFORCEMENT_CODES, type EnforcementCode, type EnforcementInfo } from './enforcement';

/**
 * PostHog tracing headers (https://posthog.com/docs/logs/link-session-replay).
 * Lets the backend link its OTel logs and $exception events to this tab's
 * session replay and person. Read directly from posthog-js (not via
 * `./analytics`) to avoid an api <-> authStore import cycle.
 */
function posthogTracingHeaders(): Record<string, string> {
  try {
    if (!import.meta.env.VITE_POSTHOG_KEY?.trim()) return {};
    const sessionId =
      typeof posthog.get_session_id === 'function' ? posthog.get_session_id() : null;
    const distinctId =
      typeof posthog.get_distinct_id === 'function' ? posthog.get_distinct_id() : null;
    const headers: Record<string, string> = {};
    if (typeof sessionId === 'string' && sessionId) headers['X-POSTHOG-SESSION-ID'] = sessionId;
    if (typeof distinctId === 'string' && distinctId) headers['X-POSTHOG-DISTINCT-ID'] = distinctId;
    return headers;
  } catch {
    return {};
  }
}

/** $exception for API 5xx / network failures. No-op without a PostHog key. */
function captureApiError(error: unknown, props: Record<string, unknown>): void {
  try {
    if (!import.meta.env.VITE_POSTHOG_KEY?.trim()) return;
    (posthog.captureException as (e: unknown, p?: Record<string, unknown>) => void)(error, props);
  } catch {
    /* never throw from the interceptor */
  }
}

/**
 * Tiny handler registry so the interceptor can notify the auth store without
 * importing it (api <-> authStore would be a circular import). The store
 * registers both handlers right after it is created.
 */
type EnforcementHandler = (info: EnforcementInfo) => void;
type SessionLostHandler = () => void;

let enforcementHandler: EnforcementHandler | null = null;
let sessionLostHandler: SessionLostHandler | null = null;

export function setEnforcementHandler(handler: EnforcementHandler | null): void {
  enforcementHandler = handler;
}

export function setSessionLostHandler(handler: SessionLostHandler | null): void {
  sessionLostHandler = handler;
}

function notifySessionLost(): void {
  try {
    sessionLostHandler?.();
  } catch {
    /* never throw from the interceptor */
  }
}

// Ensure baseURL always points to the backend '/api' prefix
const rawBase = import.meta.env.VITE_API_URL;
const baseURL = rawBase
  ? (rawBase.endsWith('/api') ? rawBase : `${rawBase.replace(/\/+$/, '')}/api`)
  : '/api';

const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
      delete (config.headers as Record<string, unknown>)['Content-Type'];
    }
    // Attach PostHog session/distinct ids so backend logs + errors link to this replay.
    try {
      const tracing = posthogTracingHeaders();
      for (const [key, value] of Object.entries(tracing)) {
        (config.headers as Record<string, unknown>)[key] = value;
      }
    } catch {
      /* never throw from the interceptor */
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 on /auth/login (etc.) means wrong password or unconfirmed email — not "session expired".
    // Do not call /auth/refresh or the user sees "Refresh token required" instead of the real error.
    const path = String(originalRequest.url || '');
    const isAuthHandoffRequest =
      path.includes('/auth/login') ||
      path.includes('/auth/register') ||
      path.includes('/auth/confirm-email') ||
      path.includes('/auth/complete-recovery') ||
      path.includes('/auth/forgot-password') ||
      path.includes('/auth/magic-link') ||
      path.includes('/auth/social-signup/pending') ||
      path.includes('/auth/social-signup/complete');

    const status: number | undefined = error.response?.status;
    const data = (error.response?.data ?? null) as
      | { code?: unknown; reason?: unknown; until?: unknown }
      | null;
    const code = data && typeof data.code === 'string' ? data.code : null;

    // Account suspended/banned, org suspended, or IP blocked: the auth store
    // hard-navigates to /account-suspended. Plain 403s (no `code`) are untouched.
    if (status === 403 && code && ENFORCEMENT_CODES.has(code)) {
      try {
        enforcementHandler?.({
          code: code as EnforcementCode,
          reason: typeof data?.reason === 'string' ? data.reason : null,
          until: typeof data?.until === 'string' ? data.until : null,
        });
      } catch {
        /* never throw from the interceptor */
      }
      return Promise.reject(error);
    }

    // Force sign-out: the session was revoked server-side. Refreshing would
    // fail with the same code, so skip it and drop the local session now.
    if (status === 401 && code === 'SESSION_REVOKED' && !isAuthHandoffRequest) {
      if (!path.includes('/auth/me')) notifySessionLost();
      return Promise.reject(error);
    }

    // If 401 and not already retried, try to refresh token
    if (
      status === 401 &&
      !originalRequest._retry &&
      !isAuthHandoffRequest &&
      !path.includes('/auth/refresh') &&
      !path.includes('/auth/me')
    ) {
      originalRequest._retry = true;

      try {
        await api.post('/auth/refresh');
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed: drop the local session so ProtectedRoute redirects
        // to /login. (/auth/me never reaches here; fetchUser handles its own catch.)
        notifySessionLost();
        return Promise.reject(refreshError);
      }
    }

    // Report server-side failures to PostHog error tracking. 4xx (except 401
    // refresh flows above) are usually user errors; 5xx and network failures
    // are the ones worth an $exception with request context. Calls posthog-js
    // directly (not via `./analytics`) to avoid an api -> analytics ->
    // authStore -> api import cycle.
    try {
      const isNetworkFailure = typeof status === 'undefined';
      if (isNetworkFailure || (typeof status === 'number' && status >= 500)) {
        captureApiError(error, {
          api_path: path.slice(0, 200),
          api_status: status ?? 'network_failure',
          api_method: String(originalRequest.method || '').toUpperCase().slice(0, 10) || null,
        });
      }
    } catch {
      /* never throw from the interceptor */
    }

    return Promise.reject(error);
  }
);

export default api;

